import { iconButtonVariants } from "@/components/Button/IconButton";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { Image01Icon } from "@/components/Icons/Image01Icon";
import { buttonVariants } from "@/design-system/Button";
import { Stack } from "@/design-system/Stack";
import { twResolve } from "@/helpers/tw-resolve";
import { useTranslation } from "@/translations";

import type { MediaBaseProps } from "./Media";
import { Media } from "./Media";
import { MediaXButton } from "./MediaXButton";

export interface ImageMediaProps extends MediaBaseProps {
  src: string | undefined;
}

export function ImageMedia({ src, ...props }: ImageMediaProps): React.ReactNode {
  return (
    <Media {...props} type="image">
      {src && <img className="relative size-full rounded-lg object-cover" src={src} alt="" />}
    </Media>
  );
}

export function AvatarMedia({
  src,
  onEdit,
  onDelete,
  onAdd,
  dropActive,
  ...props
}: Omit<ImageMediaProps, "theme" | "texts">): React.ReactNode {
  const { t } = useTranslation();

  return (
    <Stack gap="sm" align="center">
      <div
        {...props}
        className={twResolve(
          "relative size-media-preview rounded-full border border-border-secondary",
          props["aria-invalid"] && "border-border-danger! focus-within:ring-4 focus-within:ring-border-danger-tertiary",
          props.disabled && "bg-background-secondary",
          dropActive && "border-border-brand",
        )}
      >
        {src && (
          <>
            {onEdit && (
              <button
                type="button"
                aria-label={t("common.action.edit")}
                className={twResolve(
                  buttonVariants({ variant: "soft", size: "md" }),
                  iconButtonVariants({ variant: "soft", size: "md" }),
                  "absolute top-0 left-0 z-10 flex size-full! items-center justify-center rounded-full border-none bg-background-default-inverse/60! px-0 text-text-default-inverse opacity-0 transition-opacity focus-visible:opacity-100 focus-visible:ring-1 focus-visible:ring-border-tertiary hocus:opacity-100",
                )}
                data-testid={props["data-edit-testid"]}
                onClick={onEdit}
                onFocus={props.onFocus}
                onBlur={props.onBlur}
                disabled={props.disabled}
              >
                <Edit05Icon />
              </button>
            )}
            <img className="relative top-0 left-0 size-full rounded-full object-cover" {...{ src }} />
            {onDelete != null && !props.disabled && (
              <div className="absolute top-2 right-2 z-10 translate-x-1/2 -translate-y-1/2">
                <MediaXButton
                  data-testid={props["data-delete-testid"]}
                  onClick={onDelete}
                  title={t("common.action.delete")}
                />
              </div>
            )}
          </>
        )}
        {!src && (
          <button
            type="button"
            aria-label={t("common.action.add-a-photo")}
            className={twResolve(
              buttonVariants({ variant: "outline", size: "md" }),
              iconButtonVariants({ variant: "outline", size: "md" }),
              "absolute top-0 left-0 z-10 flex size-full! items-center justify-center rounded-full border-none px-0 text-text-primary/60 hover:text-text-primary focus-visible:ring-1 focus-visible:ring-border-tertiary",
            )}
            data-testid={props["data-add-testid"]}
            onClick={onAdd}
            onFocus={props.onFocus}
            onBlur={props.onBlur}
            disabled={props.disabled}
          >
            <Image01Icon />
          </button>
        )}
      </div>
    </Stack>
  );
}
